/**
 * Single source of truth for every piece of copy on the site.
 *
 * Anything marked TODO is a placeholder I filled in so the layout reads as a
 * finished page — swap them for the real details. Nothing else in `src/` hard
 * codes wedding facts, so editing this file is enough.
 */

export const couple = {
  one: { first: "Jackson", last: "Casey" }, // TODO: confirm surname
  two: { first: "Julians", last: "Krumins" }, // Latvian — the S belongs to the first name
  /** Order matters for the monogram: "J & J". */
  monogram: "J&J",
} as const;

/** Saturday. Local time, in the venue's timezone. */
export const ceremonyStart = "2027-04-03T16:00:00-05:00"; // TODO: confirm start time (Austin is CDT, UTC-5, on this date)

export const day = {
  weekday: "Saturday",
  dateLong: "the 3rd of April",
  yearLong: "2027",
  dateShort: "April 3rd, 2027",
} as const;

export const venue = {
  name: "The Chestnut Hall", // TODO
  place: "McKinney TX", // TODO
  region: "DFW Metroplex", // TODO
  address: "103 S Chestnut St, McKinney, TX 75069", // TODO
  mapUrl: "https://maps.app.goo.gl/SNwYucDhoN5qhyMJ8", // TODO
  /** [lon, lat] — MapLibre's order, not a street atlas's. See `maps` below. */
  coords: [-96.61337, 33.19711],
} as const;

export const invitation = [
  "Together with our families,",
  "we invite you to celebrate our wedding.",
  "Please join us for the ceremony and reception",
  "as we begin this next chapter together.",
] as const;

export const story = {
  eyebrow: "How we got here",
  title: "Under the Crape Myrtles",
  paragraphs: [
    "Our story started in November 2021, during Jackson’s freshman year at Texas Tech and Julians’ junior year. After matching online and chatting back and forth for a while, we finally decided to meet. Our first date took place in Jackson’s dorm room in Talkington Hall. It wasn’t the most romantic spot for a date, but something clicked, and before long, we were officially together.",
    "A few months later, during our first spring break, we visited the Dallas Arboretum and took a single photo together beneath the crape myrtles. At the time, it was simply a beautiful afternoon and one of our first little adventures together. Years later, Jackson brought Julians back to that very same spot—this time with a ring.",
    "Since that first date, we’ve grown up together in more ways than we could have imagined. We made it through the highs and lows of college, moved across the state, added two dogs to the family, and stood beside each other through everything in between. What started as a simple dorm date in Lubbock became a life we love—and we couldn’t be more excited for what comes next.",
  ],
  /**
   * Two frames of the same allée at the Dallas Arboretum, years apart. Printed
   * in matched monochrome by `scripts/photos.mjs` — in colour, one bright
   * afternoon and one overcast one refuse to sit together.
   */
  photos: [
    {
      name: "story-first-break",
      alt: "Jackson and Julians standing side by side on the path beneath the crape myrtles",
    },
    {
      name: "story-proposal",
      alt: "Jackson on one knee on the same path, holding Julians’ hand",
    },
  ],
  photoCaption:
    "The same stretch of path — the first spring, and the one with the ring.",
} as const;

/**
 * The pair that breaks the page between the countdown and the story. The
 * drawings in `public/dogs/` are derived from the originals by
 * `scripts/dogs.mjs`, which prints them in forest ink and pads them to a
 * shared height so they stand on one ground line.
 */
export const dogs = [
  {
    name: "rowan",
    alt: "Rowan, our labrador, drawn sitting",
  },
  {
    name: "winston",
    alt: "Winston, our golden retriever, drawn sitting",
  },
] as const;

export const schedule = [
  {
    time: "Five o’clock",
    title: "Ceremony",
    detail:
      "In the outdoor section of the venue. Please be seated by a quarter to five.",
  },
  {
    time: "Half past five",
    title: "Cocktails + mingling",
    detail:
      "We'll move inside for a brief cocktail hour so everyone can find their seats and get to know their tablemates!",
  },
  {
    time: "Six o’clock",
    title: "Dinner",
    detail:
      "We'll be doing the first dance, speeches, and dinner all in one go. The bar will be open throughout.",
  },
  {
    time: "Half past eight",
    title: "Dancing",
    detail: "We'll let the DJ take over and dance the night away.",
  },
  {
    time: "Half past eleven",
    title: "Farewell",
    detail:
      "The grooms will be waving goodbye and sending everyone home to their beds. Thank you for celebrating with us!",
  },
] as const;

/**
 * What happens after the farewell.
 *
 * Its own page rather than a sixth row on the schedule, because it is not part
 * of the day: this is a separate invitation to a separate room, for whoever
 * is still standing. It reads
 * differently from the rest of the site on purpose — the page is dark, the
 * heading is a neon sign, and the copy is shorter and later at night than the
 * invitation's.
 *
 * `title` is the page's real heading and the words the sign is bent into. The
 * two must not drift apart: the sign is decorative and hidden from assistive
 * technology, so this string is the only version of it a screen reader, a
 * search engine or a browser tab will ever see.
 */
export const afterparty = {
  title: "The Afterparty",
  lead: "For anyone who isn’t ready for the night to end, we’re planning a little more celebrating somewhere smaller and darker. The details are still coming together.",
  update: "Check back later for the time and place.",
  note: "No dress code, no schedule, and no obligation whatsoever. Come if the night has legs.",
} as const;

/**
 * The landing page's three doors.
 *
 * The invitation used to be one long scroll with the whole weekend on it. It
 * is now a hero, these three, and the closing band — everything else moved to
 * a page of its own. A guest who wants one answer takes one step to get it
 * rather than scrolling past everything they did not ask about on the way.
 *
 * `href` is the page a card opens, and `PageLinks` reads this same list to put
 * the other two at the foot of every page, so the site is navigable from
 * anywhere in it without going back to the front.
 */
export const gateway = [
  {
    kicker: day.dateShort,
    title: "Wedding details",
    /** Set under the title, one to a line, the way an invitation sets them. */
    lines: [venue.name, venue.place],
    body: "The order of the day, what to wear, and the answers to everything guests ask us most.",
    cta: "View the details",
    href: "/details",
  },
  {
    kicker: "Travelling to Texas?",
    title: "Travel & lodging",
    lines: [],
    body: "A limited block of rooms is held for our guests, alongside everything we know about flying in and getting around once you have landed.",
    cta: "View travel guide",
    href: "/travel",
  },
  {
    kicker: story.eyebrow,
    title: "Our story",
    lines: [],
    body: "A dorm room in Lubbock, one photograph at the arboretum, and the same stretch of path years later.",
    cta: "Read our story",
    href: "/our-story",
  },
] as const;

/**
 * What a page opens with, for the two that do not already open with a heading
 * of their own. `/our-story` is the exception: its section head names the page
 * perfectly well, and a second title above it would only say the same thing
 * twice.
 */
export const pages = {
  details: {
    eyebrow: "Everything you need to know",
    title: "Details & Info",
    lead: "What to wear, the answers to everything guests ask us, and how the evening runs.",
  },
  travel: {
    eyebrow: "Travel & lodging",
    title: "Coming to McKinney?",
    lead: "Everything you need to make three decisions: where to stay, how to get here, and whether you need a car.",
  },
} as const;

/**
 * Google Maps deep link for a place we do not have a short link for. The
 * addresses below are the facts; the URL is derived from them, so there is
 * only ever one copy of an address to keep right.
 */
const mapsUrl = (query: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

/**
 * Where to stay.
 *
 * Its own section, and its own anchor, rather than a card inside Travel: the
 * room block is the one part of the weekend with a deadline attached to it, so
 * it has to be findable without reading anything else first.
 */
export const stay = {
  eyebrow: "Where to stay",
  title: "Rooms for the weekend",
  /** One label for every "open in maps" link in the section. */
  mapLabel: "Open in maps",
  lead: [
    "We're so excited to welcome you to McKinney. For anyone travelling in, here is everything we know about where to stay.",
    "Dallas–Fort Worth is enormous and built around driving, so where you stay makes a real difference to how much of the weekend you spend in the car. We'd stay in or near McKinney, along the US 75 and SH 121 corridors south of downtown.",
  ],
  /** The block itself. The rate and the link are the only things still open. */
  block: {
    label: "Our room block",
    hotel: "Marriott Dallas Allen Hotel & Convention Center",
    place: "Allen, just south of McKinney along US 75",
    address: ["777 Watters Creek Boulevard", "Allen, TX 75013"],
    body: [
      "We've reserved a limited block of rooms at the Marriott Dallas Allen, and it's the hotel we'd point you to first. The block comes with a discounted rate and keeps a good number of our guests together in one place.",
      "The number of rooms at the group rate is limited, so book early if you'd like to stay with the block.",
    ],
    /**
     * TODO: the group rate, the cut-off date and the booking link, once the
     * hotel sends them. Until then the plate says so rather than sitting
     * there with a dead button on it.
     */
    booking: null as { label: string; href: string; note: string } | null,
    /**
     * Stands in the button's slot, so it is drawn with the weight the button
     * would have had: a guest who came to this plate to book a room has to be
     * told when to come back, not left to infer it from a line of small print.
     */
    bookingSoon: {
      eyebrow: "Booking",
      head: "Opens later this week",
      body: "We're waiting on the group rate and the booking link from the hotel. Check back after Thursday 3 September — they'll be posted here as soon as we have them.",
    },
    mapUrl: mapsUrl(
      "Marriott Dallas Allen Hotel & Convention Center, 777 Watters Creek Blvd, Allen, TX 75013",
    ),
  },
  /** Everything below is a recommendation only — no rooms are held. */
  alternates: {
    title: "Other hotels we recommend",
    intro:
      "If the block fills, or you'd rather stay somewhere else, there are plenty of hotels and vacation rentals around. Two we're happy to recommend:",
    hotels: [
      {
        name: "Denizen McKinney",
        address: ["7200 SH 121", "McKinney, TX 75070"],
        body: "A newer boutique hotel in District 121, a walkable development with restaurants, bars and places to gather right outside the door. It sits on SH 121, southwest of downtown McKinney.",
        mapUrl: mapsUrl("Denizen Hotel, 7200 SH 121, McKinney, TX 75070"),
      },
      {
        name: "Sheraton McKinney Hotel",
        address: ["1900 Gateway Boulevard", "McKinney, TX 75070"],
        body: "Right by the intersection of US 75 and SH 121, which makes it about as convenient a base as there is for getting around McKinney and everything near it.",
        mapUrl: mapsUrl(
          "Sheraton McKinney Hotel, 1900 Gateway Blvd, McKinney, TX 75070",
        ),
      },
    ],
    note: "Our room block is at the Marriott Dallas Allen only. Denizen and the Sheraton are recommendations — they are not part of the block, and you would be booking at the going rate.",
  },
  elsewhere: {
    title: "Staying somewhere else?",
    paragraphs: [
      "Vacation rentals and other hotels are absolutely an option. Just know that DFW is far more spread out than the mileage on a map suggests.",
      "If you're booking on your own, we'd stay within about fifteen miles of downtown McKinney — ideally in McKinney or Allen, and generally along US 75 or SH 121 south of downtown.",
      "We'd also avoid booking on the strength of a listing calling itself the “Dallas area”. Depending on where it actually is, and on traffic, two places that look close together on a map can be a surprisingly long drive apart.",
    ],
  },
} as const;

/**
 * Getting here, and getting around once you have.
 *
 * Four groups in one section — the airports, the ride in from each of them,
 * the week itself, and the venue — because they are one question asked four
 * times, and splitting them into four sections would bury the answers.
 */
export const travel = {
  eyebrow: "Getting here",
  title: "Flying in, and getting around",
  lead: "McKinney is served by two major airports, and for our weekend either one is a good choice. They sit in very different parts of the metroplex but are roughly the same distance from us, so we'd choose on airfare, schedule and which airline you like rather than on which looks closer.",
  airports: {
    title: "The two airports",
    list: [
      {
        code: "DFW",
        name: "Dallas/Fort Worth International",
        body: "The region's main airport, and generally the one to aim for if you're flying American, Delta, United, Alaska, Frontier, Spirit or most other major carriers. It's very large, and it's west of Dallas — but highway access and the new DART Silver Line make the McKinney area straightforward to reach.",
      },
      {
        code: "DAL",
        name: "Dallas Love Field",
        body: "The Dallas home of Southwest Airlines, and the more convenient option if that's who you're flying. Love Field is much smaller than DFW and sits closer to central Dallas.",
      },
    ],
    note: "As almost anywhere in DFW, travel times from either airport swing with traffic. Give yourself a little more room than the map promises.",
  },
  fromAirport: {
    title: "From the airport",
    groups: [
      {
        airport: "From DFW",
        options: [
          {
            title: "Rental car",
            body: "The most flexible option if you plan to explore or expect to make several trips over the weekend. DFW has a consolidated Rental Car Center, with complimentary shuttles from every terminal, and from there it's a straight drive to Allen or McKinney.",
            route: null,
          },
          {
            title: "Uber or Lyft",
            body: "A rideshare will take you straight from the airport to your hotel or rental. It's the simplest option if you aren't renting a car, though a DFW-to-McKinney ride costs rather more than a hop across town.",
            route: null,
          },
          {
            title: "DART Silver Line, then a rideshare",
            body: "The Silver Line runs from DFW Terminal B to CityLine/Bush station in Richardson, at US 75 and the President George Bush Turnpike — much closer to Allen and McKinney than the airport is. Pick up an Uber or Lyft there for the last leg north. Worth it if you'd rather not rent a car and want to cut the length and the cost of the ride.",
            /** Drawn as a strip of stops, so the transfer reads at a glance. */
            route: [
              "DFW Terminal B",
              "Silver Line",
              "CityLine/Bush",
              "Uber or Lyft",
              "Your hotel",
            ],
          },
        ],
        note: "The Silver Line is worth it from DFW. The rest of the DART network is not, for where we're all staying — beyond that one train, plan on a rental car or a rideshare.",
      },
      {
        airport: "From Love Field",
        options: [
          {
            title: "Rental car",
            body: "Again the most flexible option, and the one to take if you're planning to see Dallas or anywhere else in the metroplex while you're here.",
            route: null,
          },
          {
            title: "Uber or Lyft",
            body: "If you aren't renting, a direct rideshare is what we'd recommend from Love Field to McKinney or Allen. There's no Silver Line connection on this side of town, so it's a car either way.",
            route: null,
          },
        ],
        note: null,
      },
    ],
  },
  around: {
    title: "Getting around during your stay",
    intro:
      "Dallas–Fort Worth is built around driving, and public transport is genuinely limited for visitors staying in Allen or McKinney. Plan the weekend around a rental car or a rideshare.",
    options: [
      {
        title: "Rent a car",
        body: "The most freedom, and the right call if you:",
        list: [
          "plan to explore Dallas or the wider metroplex",
          "are staying for several days",
          "are staying somewhere other than one of the hotels above",
          "expect to be in and out a few times over the weekend",
        ],
      },
      {
        title: "Uber or Lyft",
        body: "If your weekend is mostly your hotel, downtown McKinney and the wedding itself, rideshare will cover it without a rental. It's widely available across Allen and McKinney — just allow a little extra time at weekday commuting hours.",
        list: null,
      },
      {
        title: "Public transport",
        body: "We wouldn't rely on it to get around while you're here. The one exception is the Silver Line from DFW to CityLine/Bush, which is a good way to cover the first stretch in from the airport. For everything else, a car or a rideshare.",
        list: null,
      },
    ],
  },
  venue: {
    title: "At the venue",
    body: "The venue is in historic downtown McKinney, with a free parking garage nearby that's open to all our guests, and it's easy to be dropped at by rideshare. We'll share the parking details closer to the day.",
    link: { label: "Open in maps", href: venue.mapUrl },
  },
  distances: {
    title: "A note on distances",
    paragraphs: [
      "Texas has a habit of making everything look closer on a map than it feels in the car. That's the whole reason we'd steer you towards downtown McKinney, US 75 or SH 121 rather than anywhere else in the metroplex.",
      "If you aren't sure whether a hotel or rental is in a good spot, ask us before you book. We'd far rather help you pick than have you find out your “Dallas” hotel is an hour from the wedding.",
    ],
  },
} as const;

/**
 * The decision-first travel guide `/travel` is built from.
 *
 * `stay` and `travel` above are the long-form notes this was written down
 * from. Nothing renders them any more — this is the copy guests read — but
 * they are kept as the source material behind these answers, and as the place
 * the detail lives if a question ever needs the fuller version back.
 */
export const travelPage = {
  glance: [
    {
      eyebrow: "01 · Stay",
      title: "Marriott Dallas Allen",
      body: "Our first recommendation and the location of our room block.",
    },
    {
      eyebrow: "02 · Fly",
      title: "DFW or DAL",
      body: "Check both. Choose based on airfare, schedule, and airline.",
    },
    {
      eyebrow: "03 · Get around",
      title: "Car or rideshare",
      body: "Rent if you're exploring; Uber or Lyft works well for a wedding-focused weekend.",
    },
  ],
  lodging: {
    eyebrow: "01 · Where to stay",
    title: "Rooms for the weekend",
    lead: "Stay in McKinney or Allen, preferably along US 75 or SH 121 south of downtown.",
    featured: {
      eyebrow: "Our room block · recommended first",
      name: "Marriott Dallas Allen",
      location: "Allen · US 75",
      description:
        "A convenient base just south of McKinney, with our group rate and a good number of wedding guests staying together.",
      features: [
        "Group rate",
        "Wedding guests together",
        "Convenient to McKinney",
      ],
      /**
       * The room block's booking link is deliberately not here.
       *
       * The block is a fixed number of rooms at a discounted rate, and a URL
       * in a content file is a URL in the bundle, in the page and in every
       * crawler that reads it. So the link lives in the Worker's environment
       * and is handed out to a guest who has matched an invitation — see
       * `BookingGate`, and `getBooking` in `apps/api/src/index.ts`. All that
       * is here is the copy around it.
       */
      booking: {
        eyebrow: "Booking",
        title: "Group rate available for 4/1/2027 to 4/4/2027",
        body: "Limited rooms are available at our group rate.",
        label: "Book the room block",
        /**
         * Whether the hotel has sent us the link yet, which is the difference
         * between a live button and one that reads as coming soon. Flip this
         * with the Worker's `BOOKING_URL`: set here and unset there, a guest
         * unlocks their way to the dialog's "not quite yet"; set there and
         * unset here, the button never opens at all.
         */
        available: true,
        /**
         * The dialog the button opens, for a guest we have not matched yet.
         * It apologises, because being asked to prove yourself on the way to a
         * hotel booking is a strange thing to meet — and it says what the
         * check is actually for, which is the rooms rather than the guests.
         */
        gate: {
          eyebrow: "For Security",
          title: "One quick check",
          body: "Our room block is reserved for wedding guests, so we keep the booking link tucked away from bots, crawlers, and other uninvited internet guests.",
          label: "Show me the link",
          busyLabel: "One moment…",
        },
        /**
         * Matched, link in hand, already on the way. The guest does not click
         * again — `label` is the fallback underneath, for the rare browser
         * that does not follow the redirect.
         */
        ready: {
          eyebrow: "Booking",
          title: "Taking you there",
          body: "Opening the hotel’s own booking page, with our group rate applied. Rooms at that rate are limited, so it is worth booking early.",
          label: "Open it now",
        },
        /** Matched, and there is still nothing to give them. */
        soon: {
          eyebrow: "Booking",
          title: "Not quite yet",
          body: "We are still waiting on the booking link from the hotel. Check back in a few days — it will be here as soon as they send it.",
        },
      },
    },
    alternatesHeading: "Prefer somewhere else?",
    alternates: [
      {
        name: "Denizen McKinney",
        location: "District 121 · SH 121",
        tag: "Boutique",
        description:
          "Best if you like having restaurants, bars, and places to gather right outside the hotel.",
        bestFor: "Nearby Restaurants + Driving from DFW",
      },
      {
        name: "Sheraton McKinney",
        location: "US 75 + SH 121",
        tag: "Convenient",
        description:
          "A straightforward hotel option at one of the most convenient intersections for getting around McKinney.",
        bestFor: "Minimal Driving to Venue",
      },
    ],
    note: {
      title: "Booking somewhere else?",
      body: "Stick to McKinney or Allen and generally stay near US 75 or SH 121. “Dallas area” can mean a surprisingly long drive from the wedding.",
    },
  },
  gettingHere: {
    eyebrow: "02 · Getting here",
    title: "Which airport?",
    lead: "Don't overthink it. Both airports work well for the weekend — check both and choose the better flight.",
    airports: [
      {
        code: "DFW",
        name: "Dallas/Fort Worth International",
        body: "Best starting point for American and most major airlines.",
        options: ["Rental car", "Uber / Lyft", "Silver Line"],
      },
      {
        code: "DAL",
        name: "Dallas Love Field",
        body: "Best starting point for Southwest and a much smaller airport experience.",
        options: ["Rental car", "Uber / Lyft"],
      },
    ],
    silverLine: {
      eyebrow: "From DFW without a rental car",
      title: "Use the DART Silver Line",
      body: "Only $3. This is best if your hotel is near US 75. For SH 121, a direct Uber or Lyft is simpler.",
      ariaLabel:
        "Route from DFW Airport to the hotel area using the Silver Line",
      stops: [
        { title: "DFW", detail: "Terminal B" },
        { title: "Silver Line", detail: "Train" },
        { title: "CityLine/Bush", detail: "Richardson" },
        { title: "Uber / Lyft", detail: "To your hotel" },
      ],
    },
  },
  gettingAround: {
    eyebrow: "03 · Getting around",
    title: "Do I need a rental car?",
    choices: [
      {
        eyebrow: "Probably yes",
        title: "Rent a car if…",
        reasons: [
          "You want to explore Dallas or the wider metroplex.",
          "You're staying several days.",
          "You're staying somewhere other than the recommended hotels.",
          "You expect to be in and out a few times over the weekend.",
        ],
      },
      {
        eyebrow: "Probably not",
        title: "Use Uber or Lyft if…",
        reasons: [
          "Your weekend is mostly the hotel and wedding.",
          "You're staying in McKinney or Allen.",
          "You don't mind using rideshare for a few trips.",
        ],
      },
    ],
    notes: [
      {
        eyebrow: "Public transport",
        body: "We do not recommend taking the DART light rail. The Silver Line from DFW is the useful exception.",
      },
      {
        eyebrow: "At the venue",
        body: "Historic Downtown McKinney has nearby free parking and is easy to reach by rideshare. We'll share final parking details closer to the wedding.",
      },
    ],
  },
  help: {
    eyebrow: "Still have questions?",
    title: "We can help you pick",
    body: "Please reach out to either of us (or our family) if you aren't sure whether a hotel, flight, or transportation plan makes sense, we'd be more than happy to help you pick the best option for your weekend.",
  },
} as const;

/**
 * The two maps, and everything drawn on them.
 *
 * Both are engravings — static SVG, drawn at build time by `scripts/map.mjs`
 * and hung in the same 4:5 plate as the photographs. Nothing here pans or
 * zooms, so a map is fully described by where it is centred and how much of
 * the world it takes in.
 *
 * `frame` is that: a centre and a span of longitude. The height follows from
 * the plate's ratio — see `lib/map-frame.ts`, which is the one piece of
 * arithmetic the renderer and the page both use, so that the pins land on the
 * places they name.
 *
 * Coordinates are `[longitude, latitude]`, MapLibre's order and the reverse of
 * how anybody says them out loud. They were geocoded from the addresses
 * already in this file; if an address changes, the pin does not follow it.
 *
 * `side` is which way a pin's name is set from its mark. It is a judgement
 * about that map at that size — nothing computes it — and it is here because
 * the alternative is five labels all set above their marks, two of which then
 * sit on top of each other and one of which runs off the edge.
 */
export const maps = {
  /**
   * Both airports and the wedding on one plate. What a guest is trying to see
   * here is not a street, it is how far west DFW is — so it is drawn at a size
   * where nothing below a motorway survives, and the pins do the rest.
   */
  region: {
    frame: { center: [-96.829, 33.021], span: 0.7 },
    caption: "The two airports, the hotels and the venue.",
    /** The plate itself is decorative; this is what a screen reader is told it
     *  shows, and `places` below is read to them as an ordinary list. */
    ariaLabel:
      "Map of the Dallas\u2013Fort Worth area showing both airports west and south of the venue in McKinney.",
    places: [
      {
        kind: "venue",
        side: "left",
        name: "The Chestnut Hall",
        note: "McKinney",
        coords: venue.coords,
      },
      {
        kind: "stay",
        side: "left",
        name: "Marriott Dallas Allen",
        note: "Room block",
        coords: [-96.68282, 33.09241],
      },
      {
        kind: "fly",
        side: "bottom",
        name: "DFW",
        note: "About an hour west",
        coords: [-97.04534, 32.89625],
      },
      {
        kind: "fly",
        side: "bottom",
        name: "DAL",
        note: "Love Field",
        coords: [-96.84732, 32.84524],
      },
      {
        kind: "stop",
        side: "left",
        name: "CityLine/Bush",
        note: "Silver Line",
        coords: [-96.70422, 33.00326],
      },
    ],
  },
  /**
   * McKinney and Allen, where the weekend actually happens. Drawn close enough
   * to show the block the venue is on.
   */
  venue: {
    frame: { center: [-96.659, 33.145], span: 0.16 },
    caption: "The venue, and the three hotels we recommend.",
    ariaLabel:
      "Map of McKinney and Allen showing the venue in downtown McKinney and the three recommended hotels.",
    places: [
      {
        kind: "venue",
        side: "left",
        name: "The Chestnut Hall",
        note: "Downtown McKinney",
        coords: venue.coords,
      },
      {
        kind: "stay",
        side: "top",
        name: "Marriott Dallas Allen",
        note: "Our room block",
        coords: [-96.68282, 33.09241],
      },
      {
        kind: "stay",
        side: "right",
        name: "Denizen McKinney",
        note: "District 121",
        coords: [-96.70476, 33.13634],
      },
      {
        kind: "stay",
        side: "top",
        name: "Sheraton McKinney",
        note: "Gateway Blvd",
        coords: [-96.64098, 33.16277],
      },
    ],
  },
} as const;

/** One map, as `Map` takes it. */
export type MapView = (typeof maps)[keyof typeof maps];
/** A pin. `kind` selects the mark drawn for it, `side` where its name sits. */
export type MapPlace = MapView["places"][number];

/**
 * The three things guests ask before they read anything else. Shown as a strip
 * above the FAQ; `icon` selects one of the hairline drawings in
 * `components/Glance.tsx` ("attire" | "leaf" | "clock").
 */
export const glance = [
  {
    icon: "attire",
    label: "Attire",
    value: "Cocktail",
    note: "Anything but green, please — that one is spoken for.",
  },
  {
    icon: "leaf",
    label: "Setting",
    value: "Partly outdoors",
    note: "The ceremony is outside. Dinner and dancing move indoors.",
  },
  {
    icon: "clock",
    label: "Arrive by",
    value: "Quarter to five",
    note: "The ceremony begins at five o\u2019clock sharp.",
  },
] as const;

export const details = [
  {
    q: "What should we wear?",
    a: "Cocktail attire. Suits, cocktail dresses, and slacks/pants. Please leave the green at home, as we are saving it for the wedding party. The ceremony is on soft ground, so a block heel or a flat will serve you better than a stiletto.",
  },
  {
    q: "How do we reply?",
    a: "Use the link we sent with your invitation. It already knows who is on it, so one reply covers everyone — couples do not need to fill it in twice. The link stays live until the deadline, so open it again if anything changes.",
  },
  {
    q: "Can I bring a plus one?",
    a: "If your invitation includes one, the option is there on your RSVP page — just add their name so we can seat them.",
  },
  {
    q: "Are you registered anywhere?",
    a: "We will provide a link to the registry once formal invitations are sent out.", // TODO: registry link, if you want one,
  },
  {
    q: "What is the weather doing in April?",
    a: "Warm days, cooler once the sun goes down. Bring something for your shoulders in the evening.",
  },
  {
    q: "Who do we ask about everything else?",
    a: "Please feel free to text any groom (probably Jackson), if you have any questions!", // TODO: real contact address
  },
] as const;

export const rsvp = {
  eyebrow: "Kindly reply",
  title: "Will you join us?",
  deadline: "by the first of February, 2027", // TODO
  /**
   * Dinner, chosen per person rather than per invitation.
   *
   * Two plates and a third button, and the third is the interesting one: it
   * opens the picker below rather than a blank box. That is the whole change
   * from the first cut — a guest for whom neither plate works used to explain
   * why in prose, and prose is not something a kitchen can count. Everyone
   * coming now lands on one of these nine values, and the notes box underneath
   * is optional, for whatever the nine cannot say.
   *
   * The ids are what `guests.meal` stores. The database holds the same list as
   * a CHECK constraint and the Worker a third copy for the guest list, the CSV
   * and the email — adding an option means touching all three.
   */
  meals: [
    { id: "chicken", label: "Chicken" },
    { id: "beef", label: "Beef" },
  ],
  /** The third button, which opens `diets` in a picker rather than standing for an answer. */
  mealOther: "Other",
  /**
   * What that button opens, as a required dropdown. Ordered by what the kitchen
   * has to cook differently rather than alphabetically — a picker is read top
   * to bottom, so the order is the order guests see.
   */
  diets: [
    { id: "gluten_free", label: "Celiac / Gluten Free" },
    { id: "lactose_free", label: "Lactose Free" },
    { id: "kosher", label: "Kosher" },
    { id: "halal", label: "Halal" },
    { id: "vegetarian", label: "Vegetarian" },
    { id: "vegan", label: "Vegan" },
    { id: "pescatarian", label: "Pescatarian" },
  ],
} as const;

/**
 * The bar's links, and the mobile menu's. Pages now rather than hashes, so a
 * link means the same thing from anywhere on the site.
 */
export const nav = [
  { label: "Details", href: "/details" },
  { label: "Travel", href: "/travel" },
  { label: "Our Story", href: "/our-story" },
  { label: "Afterparty", href: "/afterparty" },
] as const;

/**
 * What is not settled yet.
 *
 * The site goes up before the particulars do, so the sections that depend on
 * them stand in with a card that says so, and the reply buttons stay off until
 * invitations are out. Flip one to `false` and that section renders its real
 * content — nothing else has to change.
 *
 * Three bare constants rather than fields on the object below, and that is
 * deliberate: a `const` initialised to a literal is folded by the bundler, so
 * the branch it guards is dropped along with everything only that branch
 * reaches. Unconfirmed times and half-written answers are not merely hidden —
 * they are not in the file a browser downloads. Written as `comingSoon.schedule`
 * they would ship in full, because a property read is not something a bundler
 * can fold. The cost is that flipping one is a rebuild, which a deploy is
 * anyway.
 *
 * The RSVP flag only hides the two buttons that advertise the flow. `/rsvp`
 * itself keeps working throughout, so an invitation that goes out ahead of the
 * public prompt can be replied to by anyone typed the address by hand.
 */
/** The order of the day — times are not confirmed. */
export const SCHEDULE_COMING_SOON = true;
/** The at-a-glance strip and the FAQ. */
export const DETAILS_COMING_SOON = true;
/** The RSVP buttons in the nav and the closing band. */
export const RSVP_COMING_SOON = false;
/** The placeholder's own copy. */
export const comingSoon = {
  eyebrow: "Coming soon",
  /** One line per placeholder, in the voice of the section it stands in for. */
  note: {
    schedule:
      "We're still finalising the order of the day and planning all the details, but we promise to share it here as soon as we can.",
    details:
      "We'll have the final details, including attire and the RSVP, ready to share once invitations are sent. Travel and lodging have a page of their own. Please check back soon!",
  },
  /** Stands in for the reply prompt while the buttons are off. */
  rsvpNote: "Invitations, and how to reply, will follow.",
} as const;

/**
 * Everything a link preview shows — the page title, the description, and the
 * Open Graph card. `vite.config.ts` reads this at build time and writes it
 * into `index.html`, which is why no copy lives in that file.
 *
 * One card for the whole site, because every page of it is the same invitation
 * now. There used to be a second, personalised one: a bespoke invitation link
 * previewed as "Amelia & Tom — you are invited", written into the head at the
 * edge by the Worker in front of `/rsvp/*`. Those links are gone and so is
 * that route.
 */
export const share = {
  main: {
    title: "Jackson & Julians — 3 April 2027",
    description:
      "Jackson Casey and Julians Krumins are getting married on Saturday, the 3rd of April, 2027.",
    ogTitle: "Jackson & Julians",
    ogDescription: "Saturday, the 3rd of April, 2027.",
    image: "/social-preview.jpg",
    imageWidth: 1200,
    imageHeight: 630,
    imageAlt:
      "Jackson and Julians beneath the crape myrtles, overlaid with their J and J wedding monogram",
  },
} as const;
